// CellRenderer/CellContentDialog/index.tsx
// Unified Cell Content Dialog — view and edit any cell type in a spacious modal.
//
// Always WYSIWYG: editable cells open directly in their editor (TipTap for
// markdown, formatted textarea for JSON, plain textarea for text). No mode
// toggling — the editing surface IS the viewing surface (Notion-style).
// Computed/readonly columns open as rendered views.

import type { GridApi } from "ag-grid-community";
import { observer } from "mobx-react-lite";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";

import { ASSERTION_DISPLAY_MODE } from "../assertions";
import { extractCompareDiff } from "../utils";

import { TextEditor } from "./CellContentEditor/TextEditor";
import CellContentHeader from "./CellContentHeader";
import CellContentRenderer from "./CellContentRenderer";
import { AssertionRenderer } from "./CellContentRenderer/AssertionRenderer";
import { CellDiffRenderer, type CellDiffValue } from "./CellContentRenderer/CellDiffRenderer";
import CellContentStatusBar from "./CellContentStatusBar";
import {
  CellContentDialogProvider,
  type CellContentDialogContextValue,
} from "./cell-content-dialog-context";
import {
  nextDisplayTypeOverride,
  useCellContentDialog,
  useDialogColumnTarget,
  useDialogContentModel,
  useDialogEditPipeline,
  useGridDialogFocusRestore,
} from "./hooks";
import {
  REQUEST_LOG_DISPLAY_MODE,
  TRACE_DISPLAY_MODE,
  extractTraceCellDisplayName,
  formatPromptCellSnippets,
  getDataRowIndex,
  getDisplayedRowNodeByDataRowIndex,
  getAnchorCellViewId,
  getPromptCellSnippets,
  getRequestCellViewId,
  shouldRenderLargeJsonAsRaw,
} from "./utils";

import { MockRequestCellView } from "@/components/RequestDisplay/MockRequestCellView";
import { MockTraceCellView } from "@/components/TraceDetail/MockTraceCellView";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetCell, useUpdateCell } from "@/queries/smart-tables";
import type { SmartColumn } from "@/queries/smart-tables/types";
import { CellStatus, ColumnType, isTruncatedValueStub } from "@/queries/smart-tables/types";
import { useSheetDataStoreSafe } from "@/routes/smart-table/sheet-data-store-context";
import { resolveCellDisplayState } from "@/routes/smart-table/utils/cellDisplayState";
import { getCompositionSourceTitle } from "@/routes/smart-table/utils/compositionColumns";
import {
  isRequestColumnType,
  isTraceColumnType,
} from "@/routes/smart-table/utils/columnTypeGuards";
import { CellContentType } from "@/routes/smart-table/utils/contentType";
import { displayErrorToast } from "@/utils/toast";

const COMPARE_TEXT_DISPLAY_MODE = "compare_text" as const;

// ── Constants ────────────────────────────────────────────────────────────────

/** Set-based media type lookup — avoids per-render array allocation. */
const MEDIA_CONTENT_TYPES = new Set([
  CellContentType.IMAGE,
  CellContentType.AUDIO,
  CellContentType.VIDEO,
  CellContentType.PDF,
  CellContentType.FILE,
  CellContentType.MEDIA_COLLECTION,
  CellContentType.MULTI_CONTENT,
]);

const PREVIEW_CONTENT_TYPES = new Set([...MEDIA_CONTENT_TYPES, CellContentType.LINK]);

const LazyMarkdownTiptapEditor = lazy(() =>
  import("./CellContentEditor/MarkdownTiptapEditor").then(module => ({
    default: module.MarkdownTiptapEditor,
  })),
);

const LazyJsonEditor = lazy(() =>
  import("./CellContentEditor/JsonEditor").then(module => ({
    default: module.JsonEditor,
  })),
);

const getDisplayedDataRowIndex = (api: GridApi, displayedRowIndex: number): number | null =>
  getDataRowIndex(api.getDisplayedRowAtIndex(displayedRowIndex)?.data);

const isEditableNavigationTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
    return true;
  }

  return target.isContentEditable;
};

function DialogEditorSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="flex h-full min-h-[200px] w-full flex-col gap-3 rounded-lg border border-border/40 bg-muted/10 p-4 shadow-sm"
    >
      <Skeleton className="h-4 w-1/4 rounded-full bg-gradient-to-r from-muted via-muted/60 to-muted" />
      <Skeleton className="min-h-0 w-full flex-1 rounded-lg bg-gradient-to-r from-muted via-muted/60 to-muted" />
    </div>
  );
}

const getMediaContentMountKey = (content: {
  mediaPayload?: unknown;
  multiContentPayload?: unknown;
}) => {
  const { mediaPayload, multiContentPayload } = content;
  if (Array.isArray(mediaPayload)) {
    return mediaPayload
      .map(item => {
        if (!item || typeof item !== "object") {
          return "media";
        }
        const media = item as { storage_key?: unknown; url?: unknown; title?: unknown };
        return String(media.storage_key ?? media.url ?? media.title ?? "media");
      })
      .join("|");
  }

  if (mediaPayload && typeof mediaPayload === "object") {
    const media = mediaPayload as { storage_key?: unknown; url?: unknown; title?: unknown };
    return String(media.storage_key ?? media.url ?? media.title ?? "media");
  }

  if (multiContentPayload && typeof multiContentPayload === "object") {
    const { items } = multiContentPayload as { items?: unknown };
    if (Array.isArray(items)) {
      return items
        .filter(item => item && typeof item === "object" && "type" in item)
        .map(item => {
          const media = item as {
            type?: unknown;
            storage_key?: unknown;
            url?: unknown;
            title?: unknown;
          };
          const { storage_key: storageKey, title, type, url } = media;
          return type === "output_media"
            ? String(storageKey ?? url ?? title ?? "media")
            : String(type ?? "content");
        })
        .join("|");
    }
  }

  return "media";
};

// ── Types ────────────────────────────────────────────────────────────────────

export interface CellContentDialogProps {
  tableId: string | undefined;
  sheetId: string | undefined;
  versionId?: string | null;
  gridApiRef: RefObject<GridApi | null>;
  dialog: ReturnType<typeof useCellContentDialog>;
  canEditCell: (rowIndex: number | null | undefined, columnId: string | undefined) => boolean;
  hideRowNavigation?: boolean;
  hideSavedAt?: boolean;
  headerContextLabel?: string;
}

// ── Main Dialog ──────────────────────────────────────────────────────────────

export const CellContentDialog = observer(
  ({
    tableId,
    sheetId,
    versionId,
    gridApiRef,
    dialog,
    canEditCell,
    hideRowNavigation = false,
    hideSavedAt = false,
    headerContextLabel,
  }: CellContentDialogProps) => {
    const sheetDataStore = useSheetDataStoreSafe();
    const updateCellMutation = useUpdateCell();
    const updateCellMutationRef = useRef(updateCellMutation);
    updateCellMutationRef.current = updateCellMutation;

    const { isOpen, rowIndex, columnId, shouldFocusEditorOnOpen, open, close, navigateRow } =
      dialog;
    const [pendingDisplayedRowIndex, setPendingDisplayedRowIndex] = useState<number | null>(null);
    const [isDeferredMediaReady, setIsDeferredMediaReady] = useState(false);
    const [readyJsonEditorKey, setReadyJsonEditorKey] = useState<string | null>(null);
    const dialogContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!isOpen) {
        setReadyJsonEditorKey(null);
      }
    }, [isOpen]);

    const api = gridApiRef.current;
    const dialogColDef = columnId ? api?.getColumnDef(columnId) : undefined;
    const dialogColumnContext = dialogColDef?.context as
      | {
          columnType?: unknown;
          metricKey?: unknown;
          parentColumnId?: unknown;
          compositionSource?: unknown;
        }
      | undefined;
    const dialogRendererParams = dialogColDef?.cellRendererParams as
      | {
          columnType?: unknown;
          metricKey?: unknown;
          parentColumnId?: unknown;
          compositionSource?: unknown;
        }
      | undefined;
    const rawDialogParentColumnId =
      dialogRendererParams?.parentColumnId ?? dialogColumnContext?.parentColumnId;
    const dialogParentColumnId =
      typeof rawDialogParentColumnId === "string" ? rawDialogParentColumnId : undefined;
    const rawDialogCompositionSource =
      dialogRendererParams?.compositionSource ?? dialogColumnContext?.compositionSource;
    const dialogCompositionSource =
      typeof rawDialogCompositionSource === "string" ? rawDialogCompositionSource : undefined;
    const rawDialogColumnType = dialogRendererParams?.columnType ?? dialogColumnContext?.columnType;
    const dialogRendererColumnType =
      typeof rawDialogColumnType === "string" &&
      Object.values(ColumnType).includes(rawDialogColumnType as ColumnType)
        ? (rawDialogColumnType as ColumnType)
        : undefined;
    const rawDialogMetricKey = dialogRendererParams?.metricKey ?? dialogColumnContext?.metricKey;
    const dialogMetricKey = typeof rawDialogMetricKey === "string" ? rawDialogMetricKey : null;

    const columnTarget = useDialogColumnTarget({
      columnId,
      dialogParentColumnId,
      dialogCompositionSource,
      columns: sheetDataStore?.columns,
    });
    const { storageColumnId, displayColumnId, compositionSource } = columnTarget;

    // ── Display type override ────────────────────────────────────────────────
    // Lets the user switch between text-based display types (e.g. view JSON as
    // plain text, view markdown source, etc.). Resets when navigating cells.
    const [displayTypeOverride, setDisplayTypeOverride] = useState<
      CellContentType | typeof ASSERTION_DISPLAY_MODE | null
    >(null);
    const [compareTextMode, setCompareTextMode] = useState(false);

    // ── Derive reactive cell data from MobX ──────────────────────────────────

    const fallbackTotalRows = api?.getDisplayedRowCount() ?? 0;
    const idRowNode = rowIndex !== undefined ? api?.getRowNode(String(rowIndex)) : undefined;
    const resolvedRowNode =
      idRowNode ?? (api ? getDisplayedRowNodeByDataRowIndex(api, rowIndex) : undefined);
    const idDisplayedRowIndex =
      typeof resolvedRowNode?.rowIndex === "number" ? resolvedRowNode.rowIndex : null;
    const activeDisplayedRowIndex = pendingDisplayedRowIndex ?? idDisplayedRowIndex;
    const hasDisplayedRowTarget = Boolean(api && activeDisplayedRowIndex !== null);
    const displayedRowNode =
      api && activeDisplayedRowIndex !== null
        ? api.getDisplayedRowAtIndex(activeDisplayedRowIndex)
        : undefined;
    const displayedDataRowIndex = getDataRowIndex(displayedRowNode?.data);
    const isDisplayedRowReady = !hasDisplayedRowTarget || displayedDataRowIndex === rowIndex;
    const isResolvingDisplayedRow = pendingDisplayedRowIndex !== null || !isDisplayedRowReady;
    let rowNode = resolvedRowNode;
    if (isResolvingDisplayedRow) {
      rowNode = undefined;
    } else if (displayedDataRowIndex === rowIndex) {
      rowNode = displayedRowNode;
    }
    const visibleRowIndex = activeDisplayedRowIndex ?? rowIndex;
    const rowData = rowNode?.data;
    const diffMetadata = (rowData?.[`_diff_meta_${storageColumnId}`] ??
      rowData?.[`_diff_meta_${displayColumnId}`]) as CellDiffValue | null | undefined;
    const colDef = displayColumnId === columnId ? dialogColDef : api?.getColumnDef(displayColumnId);

    const column = sheetDataStore?.columns.get(storageColumnId) as SmartColumn | undefined;
    const rowMap = isResolvingDisplayedRow ? undefined : sheetDataStore?.rows.get(rowIndex);
    const cell = rowMap?.get(storageColumnId);
    const isCompareColumn =
      dialogRendererColumnType === ColumnType.COMPARE || column?.type === ColumnType.COMPARE;

    const rawCompareValue =
      cell?.value ?? rowData?.[`_value_${storageColumnId}`] ?? rowData?.[storageColumnId];
    const rawCompareDisplayValue =
      rowData?.[`_display_${storageColumnId}`] ??
      rowData?.[displayColumnId] ??
      rowData?.[storageColumnId];
    const rawCompareDisplayText = useMemo(() => {
      if (
        !isCompareColumn ||
        rawCompareDisplayValue === null ||
        rawCompareDisplayValue === undefined
      ) {
        return undefined;
      }

      if (typeof rawCompareDisplayValue === "string") {
        return rawCompareDisplayValue;
      }

      try {
        return JSON.stringify(rawCompareDisplayValue, null, 2);
      } catch {
        return String(rawCompareDisplayValue);
      }
    }, [isCompareColumn, rawCompareDisplayValue]);
    const fallbackCompareDiff = useMemo(
      () => (isCompareColumn ? extractCompareDiff(rawCompareValue) : null),
      [isCompareColumn, rawCompareValue],
    );

    const compareDiff = useMemo<CellDiffValue | null>(() => {
      const hasVersionDiff =
        Boolean(diffMetadata) &&
        (diffMetadata?.beforeValue ?? diffMetadata?.beforePreview ?? "") !==
          (diffMetadata?.afterValue ?? diffMetadata?.afterPreview ?? "");

      if (hasVersionDiff && diffMetadata) {
        return {
          ...diffMetadata,
          rawDiffText: diffMetadata.rawDiffText ?? rawCompareDisplayText,
        };
      }

      if (!fallbackCompareDiff) {
        if (!rawCompareDisplayText) {
          return null;
        }

        return {
          beforeValue: "",
          afterValue: "",
          rawDiffText: rawCompareDisplayText,
        };
      }

      return {
        beforeValue: fallbackCompareDiff.beforeValue,
        afterValue: fallbackCompareDiff.afterValue,
        rawDiffText: rawCompareDisplayText,
      };
    }, [diffMetadata, fallbackCompareDiff, rawCompareDisplayText]);

    const shouldRenderDiffContent =
      Boolean(compareDiff) &&
      (compareDiff?.beforeValue ?? compareDiff?.beforePreview ?? "") !==
        (compareDiff?.afterValue ?? compareDiff?.afterPreview ?? "");
    const shouldRenderDiffViewer = shouldRenderDiffContent && isCompareColumn && !compareTextMode;

    useEffect(() => {
      setDisplayTypeOverride(null);
      setCompareTextMode(false);
    }, [isOpen, rowIndex, displayColumnId]);

    const totalRows = fallbackTotalRows || sheetDataStore?.totalRows || 0;
    const headerColumn = useMemo(() => {
      const fallbackHeaderColumn =
        !column && colDef?.headerName
          ? ({
              id: displayColumnId,
              title: colDef.headerName,
            } as SmartColumn)
          : undefined;

      return column && compositionSource !== undefined
        ? ({
            ...column,
            title: getCompositionSourceTitle(column, compositionSource) ?? column.title,
          } as SmartColumn)
        : (column ?? fallbackHeaderColumn);
    }, [column, colDef?.headerName, compositionSource, displayColumnId]);

    const rowStatus = rowData?.[`_status_${storageColumnId}`] as CellStatus | undefined;
    const residentCellValue = cell?.value ?? rowData?.[`_value_${storageColumnId}`];
    const isResidentContentTruncated =
      isTruncatedValueStub(residentCellValue) ||
      cell?.display_value_truncated === true ||
      rowData?.[`_displayTruncated_${storageColumnId}`] === true;
    const virtualCellCoordinates =
      !versionId && (cell?.is_ref || cell?.is_virtual)
        ? { columnId: cell.smart_column_id, rowIndex: cell.row_index }
        : undefined;
    const versionCellCoordinates = versionId
      ? { versionId, columnId: storageColumnId, rowIndex }
      : undefined;
    const fullCellQuery = useGetCell(
      tableId,
      sheetId,
      isOpen && isResidentContentTruncated && !versionId ? cell?.id : undefined,
      {
        virtualCell: virtualCellCoordinates,
        versionCell: isOpen && isResidentContentTruncated ? versionCellCoordinates : undefined,
      },
    );
    const fetchedFullCell =
      fullCellQuery.data && !isTruncatedValueStub(fullCellQuery.data.value)
        ? fullCellQuery.data
        : undefined;
    const renderLargeJsonAsRaw = shouldRenderLargeJsonAsRaw(
      isResidentContentTruncated,
      fetchedFullCell !== undefined,
    );
    const dialogCell = fetchedFullCell ?? cell;
    const status: CellStatus = dialogCell?.status ?? rowStatus ?? CellStatus.STALE;
    const isFullCellUnavailable = isOpen && isResidentContentTruncated && !fetchedFullCell;
    const didFullCellFetchSettleWithoutValue =
      fullCellQuery.isSuccess &&
      fullCellQuery.isFetchedAfterMount &&
      !fullCellQuery.isFetching &&
      !fetchedFullCell;
    const isFullCellPaused = isFullCellUnavailable && fullCellQuery.fetchStatus === "paused";
    const isFullCellFetchError =
      isFullCellUnavailable &&
      (!fullCellQuery.isEnabled || fullCellQuery.isError || didFullCellFetchSettleWithoutValue);
    const isFullCellLoading = isFullCellUnavailable && !isFullCellFetchError;

    useEffect(() => {
      if (!isFullCellFetchError) {
        return;
      }

      displayErrorToast("Failed to load the full cell value. The preview is still available.");
    }, [isFullCellFetchError]);

    const contentApi = isFullCellLoading ? undefined : api;
    const contentRowNode = isFullCellLoading ? undefined : rowNode;
    const contentRowData = isFullCellLoading ? undefined : rowData;
    const contentCell = isFullCellLoading ? undefined : dialogCell;
    const displayState = useMemo(
      () =>
        resolveCellDisplayState(
          contentCell ?? {
            status,
            display_value:
              contentRowData?.[`_display_${displayColumnId}`] ??
              contentRowData?.[`_display_${storageColumnId}`],
            value:
              contentRowData?.[`_value_${displayColumnId}`] ??
              contentRowData?.[`_value_${storageColumnId}`] ??
              contentRowData?.[displayColumnId] ??
              contentRowData?.[storageColumnId],
          },
        ),
      [contentCell, contentRowData, displayColumnId, status, storageColumnId],
    );

    const {
      canEdit,
      assertionModel,
      content,
      defaultDisplayType,
      displayType,
      displayValue,
      effectiveContent,
      effectiveStatus,
      shouldShowComputationStatus,
    } = useDialogContentModel({
      api: contentApi,
      rowNode: isResolvingDisplayedRow ? undefined : contentRowNode,
      rowData: contentRowData,
      column,
      cell: contentCell,
      hasFetchedFullCell: fetchedFullCell !== undefined,
      status,
      displayState,
      displayColumnId,
      storageColumnId,
      compositionSource,
      dialogRendererColumnType,
      metricKey: dialogMetricKey,
      displayTypeOverride,
      canEditCell: (targetRowIndex, targetColumnId) =>
        !isFullCellUnavailable && canEditCell(targetRowIndex, targetColumnId),
      rowIndex,
    });
    const jsonEditorKey = `${rowIndex}:${displayColumnId}`;
    const isJsonEditorLoading =
      isOpen &&
      !isFullCellLoading &&
      canEdit &&
      displayType === CellContentType.JSON &&
      readyJsonEditorKey !== jsonEditorKey;
    const isDialogContentLoading = isFullCellLoading || isJsonEditorLoading;

    // ── Save pipeline ────────────────────────────────────────────────────────
    //
    // Same optimistic-update paradigm as AG Grid inline editing:
    //   1. User types → local React state only, NO store writes during typing.
    //   2. User closes (Done / navigate row / close dialog) →
    //      snapshot → optimistic store update → single mutation → reconcile/rollback.

    const { editValue, editValueRef, handleEditChange, handleClose, commitEdit, isSaving } =
      useDialogEditPipeline({
        canEdit,
        cell: dialogCell,
        tableId,
        sheetId,
        sheetDataStore,
        displayType:
          displayType === ASSERTION_DISPLAY_MODE ? CellContentType.PLAIN_TEXT : displayType,
        displayValue,
        isOpen,
        rowIndex,
        displayColumnId,
        onCloseDialog: close,
        commitToServer: (mutationArgs, snapshot, callbacks) => {
          let nextMutationArgs = mutationArgs;
          if (isTraceColumnType(column?.type) && mutationArgs.value !== undefined) {
            const traceDisplayName = extractTraceCellDisplayName(mutationArgs.value);
            if (traceDisplayName) {
              nextMutationArgs = {
                ...mutationArgs,
                display_value: traceDisplayName,
              };
              sheetDataStore?.updateCell(mutationArgs.cellId, traceDisplayName, mutationArgs.value);
            }
          }

          updateCellMutationRef.current.mutate(nextMutationArgs, {
            onSuccess: serverCell => {
              sheetDataStore?.reconcileCell(serverCell);
              callbacks.onSuccess();
            },
            onError: () => {
              sheetDataStore?.restoreCellSnapshot(snapshot);
              displayErrorToast("Failed to save cell — your changes were reverted.");
              callbacks.onError();
            },
          });
        },
      });

    // ── Row navigation ────────────────────────────────────────────────────────

    // Ref for the scrollable content container — used to reset scroll on navigation.
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const navigate = useCallback(
      (delta: number) => {
        if (canEdit) {
          commitEdit();
        }
        editValueRef.current = "";
        setCompareTextMode(false);
        scrollContainerRef.current?.scrollTo({ top: 0 });

        const displayedRowCount = api?.getDisplayedRowCount() ?? 0;
        if (api && activeDisplayedRowIndex !== null && displayedRowCount > 0) {
          const nextDisplayedRowIndex = Math.max(
            0,
            Math.min(activeDisplayedRowIndex + delta, displayedRowCount - 1),
          );

          if (nextDisplayedRowIndex === activeDisplayedRowIndex) {
            return;
          }

          api.ensureIndexVisible(nextDisplayedRowIndex, "middle");
          const nextDataRowIndex = getDisplayedDataRowIndex(api, nextDisplayedRowIndex);

          if (nextDataRowIndex !== null) {
            setPendingDisplayedRowIndex(null);
            open(nextDataRowIndex, columnId, { focusEditor: false });
            return;
          }

          setPendingDisplayedRowIndex(nextDisplayedRowIndex);
          return;
        }

        navigateRow(delta);
      },
      [
        activeDisplayedRowIndex,
        api,
        canEdit,
        columnId,
        commitEdit,
        editValueRef,
        navigateRow,
        open,
      ],
    );

    // Radix owns focus while the modal is open. Restore AG Grid focus and
    // selection only after close so grid updates cannot compete with dialog paint.
    const { handleCloseAutoFocus } = useGridDialogFocusRestore({
      api,
      isDialogOpen: isOpen,
      displayedRowIndex: activeDisplayedRowIndex,
      displayColumnId,
    });

    useEffect(() => {
      if (!isOpen || !api || pendingDisplayedRowIndex === null) {
        return;
      }

      api.ensureIndexVisible(pendingDisplayedRowIndex, "middle");

      const resolvePendingRow = () => {
        const nextDataRowIndex = getDisplayedDataRowIndex(api, pendingDisplayedRowIndex);
        if (nextDataRowIndex === null) {
          return;
        }

        setPendingDisplayedRowIndex(null);
        open(nextDataRowIndex, columnId, { focusEditor: false });
      };

      resolvePendingRow();
      api.addEventListener("modelUpdated", resolvePendingRow);

      return () => {
        api.removeEventListener("modelUpdated", resolvePendingRow);
      };
    }, [api, columnId, isOpen, open, pendingDisplayedRowIndex]);

    useEffect(() => {
      if (!isOpen) {
        setPendingDisplayedRowIndex(null);
      }
    }, [isOpen]);

    // ── Keyboard handler ──────────────────────────────────────────────────────

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      const onWindowKeyDown = (event: KeyboardEvent) => {
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) {
          return;
        }

        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
          return;
        }

        if (isEditableNavigationTarget(event.target)) {
          return;
        }

        event.preventDefault();
        navigate(event.key === "ArrowUp" ? -1 : +1);
      };

      window.addEventListener("keydown", onWindowKeyDown);

      return () => {
        window.removeEventListener("keydown", onWindowKeyDown);
      };
    }, [isOpen, navigate]);

    // ── Layout ────────────────────────────────────────────────────────────────

    const hasMultiContentMedia =
      displayType === CellContentType.MULTI_CONTENT &&
      Boolean(
        effectiveContent.multiContentPayload?.items.some(item => item.type === "output_media"),
      );

    const isMediaContent =
      displayType !== ASSERTION_DISPLAY_MODE &&
      MEDIA_CONTENT_TYPES.has(displayType) &&
      (displayType !== CellContentType.MULTI_CONTENT || hasMultiContentMedia);

    const mediaContentMountKey = isMediaContent
      ? `${rowIndex}:${displayColumnId}:${getMediaContentMountKey(content)}`
      : "";

    const isLinkContent =
      displayType !== ASSERTION_DISPLAY_MODE && displayType === CellContentType.LINK;
    const isRichPreviewContent = isMediaContent || isLinkContent;
    const isExecutionMetricContent =
      displayType === CellContentType.PRICE || displayType === CellContentType.DURATION;

    let contentWrapperClass = "mx-auto w-full max-w-prose px-8 py-6";
    if (isRichPreviewContent) {
      contentWrapperClass = "flex w-full flex-col items-center px-6 py-6";
    } else if (isExecutionMetricContent) {
      contentWrapperClass = "flex min-h-0 w-full flex-1 items-center justify-center px-8 py-6";
    }

    const isRowHydrating =
      rowData?._pendingHydration === true ||
      rowData?.[`_pendingHydration_${storageColumnId}`] === true;
    const isRowLoaded =
      !isResolvingDisplayedRow && !isRowHydrating && Boolean(dialogCell || rowData);

    useEffect(() => {
      if (!isOpen || !isMediaContent || !isRowLoaded) {
        setIsDeferredMediaReady(false);
        return;
      }

      setIsDeferredMediaReady(false);
      let firstFrameId = 0;
      let secondFrameId = 0;

      firstFrameId = requestAnimationFrame(() => {
        secondFrameId = requestAnimationFrame(() => {
          setIsDeferredMediaReady(true);
        });
      });

      return () => {
        cancelAnimationFrame(firstFrameId);
        cancelAnimationFrame(secondFrameId);
      };
    }, [isMediaContent, isOpen, isRowLoaded, mediaContentMountKey]);

    const handleOpenAutoFocus = useCallback(
      (event: Event) => {
        event.preventDefault();

        if (canEdit && shouldFocusEditorOnOpen) {
          return;
        }

        requestAnimationFrame(() => {
          dialogContentRef.current?.focus({ preventScroll: true });
        });
      },
      [canEdit, shouldFocusEditorOnOpen],
    );

    // ── Render ────────────────────────────────────────────────────────────────

    useEffect(() => {
      if (!isOpen || !canEdit) {
        return;
      }

      void import("./CellContentEditor/MarkdownTiptapEditor");
      void import("./CellContentEditor/JsonEditor");
    }, [canEdit, isOpen]);

    const rawAnchorCellValue = cell?.value ?? rowData?.[`_value_${storageColumnId}`];
    const mockTraceId = getAnchorCellViewId(rawAnchorCellValue, dialogMetricKey);
    const requestLogId = getRequestCellViewId(rawAnchorCellValue, dialogMetricKey);
    const isRequestColumn =
      isRequestColumnType(column?.type) || isRequestColumnType(dialogRendererColumnType);
    const isRequestLogMode =
      isRequestColumn && requestLogId !== null && displayTypeOverride === null;
    const isTraceMode = !isRequestLogMode && mockTraceId !== null && displayTypeOverride === null;
    const promptCellSnippets =
      column?.type === ColumnType.PROMPT_TEMPLATE
        ? getPromptCellSnippets(dialogCell?.value ?? rawAnchorCellValue)
        : [];
    const snippetVersionsLabel =
      promptCellSnippets.length > 0 ? formatPromptCellSnippets(promptCellSnippets) : undefined;

    const renderContent = () => {
      if (!isRowLoaded || isFullCellLoading || (isMediaContent && !isDeferredMediaReady)) {
        return (
          <div className="mx-auto flex min-h-0 w-full max-w-prose flex-1 px-8 py-6">
            <DialogEditorSkeleton />
          </div>
        );
      }

      if (isRequestLogMode && requestLogId) {
        return (
          <div className="px-6 py-6">
            <MockRequestCellView requestId={requestLogId} />
          </div>
        );
      }

      if (isTraceMode && mockTraceId) {
        return (
          <div className="px-6 py-6">
            <MockTraceCellView traceId={mockTraceId} />
          </div>
        );
      }

      if (shouldRenderDiffViewer && compareDiff) {
        return (
          <div className={contentWrapperClass}>
            <CellDiffRenderer diff={compareDiff} />
          </div>
        );
      }

      if (isCompareColumn && shouldRenderDiffContent && compareTextMode && compareDiff) {
        return (
          <div className="px-8 py-6">
            <CellDiffRenderer diff={compareDiff} mode="text" />
          </div>
        );
      }

      // ── Editable text cells normally open in an editor. Structured media cells
      // default to preview mode, while JSON/Text modes remain editable.
      if (canEdit) {
        const isStructuredPreviewCell = PREVIEW_CONTENT_TYPES.has(content.type);
        const shouldPreviewStructuredContent =
          isStructuredPreviewCell &&
          displayType !== CellContentType.JSON &&
          displayType !== CellContentType.PLAIN_TEXT;

        if (shouldPreviewStructuredContent) {
          return (
            <div className={contentWrapperClass}>
              <CellContentRenderer
                content={effectiveContent}
                renderLargeJsonAsRaw={renderLargeJsonAsRaw}
              />
            </div>
          );
        }

        let editorContent: React.ReactNode;

        if (displayType === CellContentType.MARKDOWN) {
          editorContent = (
            <Suspense fallback={<DialogEditorSkeleton />}>
              <LazyMarkdownTiptapEditor
                key={`${rowIndex}-${displayColumnId}`}
                value={editValue}
                onChange={handleEditChange}
                focusOnMount={shouldFocusEditorOnOpen}
              />
            </Suspense>
          );
        } else if (displayType === CellContentType.JSON) {
          editorContent = (
            <Suspense fallback={<DialogEditorSkeleton />}>
              <LazyJsonEditor
                value={editValue}
                onChange={handleEditChange}
                focusOnMount={shouldFocusEditorOnOpen}
                loading={<DialogEditorSkeleton />}
                onReady={() => setReadyJsonEditorKey(jsonEditorKey)}
              />
            </Suspense>
          );
        } else {
          editorContent = (
            <TextEditor
              value={editValue}
              onChange={handleEditChange}
              focusOnMount={shouldFocusEditorOnOpen}
            />
          );
        }

        return (
          <div className="mx-auto flex min-h-0 w-full max-w-prose flex-1 px-8 py-6">
            {editorContent}
          </div>
        );
      }

      // ── Readonly: rendered view for computed columns ─────────────────────
      if (
        displayType === ASSERTION_DISPLAY_MODE &&
        assertionModel &&
        effectiveContent.type !== CellContentType.ERROR
      ) {
        return (
          <div className={contentWrapperClass}>
            <AssertionRenderer model={assertionModel} />
          </div>
        );
      }

      return (
        <div className={contentWrapperClass}>
          <CellContentRenderer
            content={effectiveContent}
            renderLargeJsonAsRaw={renderLargeJsonAsRaw}
          />
        </div>
      );
    };

    let selectedDisplayType:
      | CellContentType
      | typeof ASSERTION_DISPLAY_MODE
      | typeof TRACE_DISPLAY_MODE
      | typeof REQUEST_LOG_DISPLAY_MODE
      | "compare_text" = displayType;

    if (isCompareColumn && shouldRenderDiffContent) {
      selectedDisplayType = compareTextMode
        ? COMPARE_TEXT_DISPLAY_MODE
        : CellContentType.PLAIN_TEXT;
    } else if (isRequestLogMode) {
      selectedDisplayType = REQUEST_LOG_DISPLAY_MODE;
    } else if (isTraceMode) {
      selectedDisplayType = TRACE_DISPLAY_MODE;
    }

    const displayTypeOptions = useMemo(() => {
      if (isRequestColumn && requestLogId) {
        return [
          { value: REQUEST_LOG_DISPLAY_MODE, label: "Request Log" },
          { value: CellContentType.PLAIN_TEXT, label: "Text" },
          { value: CellContentType.MARKDOWN, label: "Markdown" },
          { value: CellContentType.JSON, label: "JSON" },
        ];
      }

      if (mockTraceId) {
        return [
          { value: TRACE_DISPLAY_MODE, label: "Trace" },
          { value: CellContentType.PLAIN_TEXT, label: "Text" },
          { value: CellContentType.MARKDOWN, label: "Markdown" },
          { value: CellContentType.JSON, label: "JSON" },
        ];
      }

      if (assertionModel !== undefined) {
        return [
          { value: ASSERTION_DISPLAY_MODE, label: "Assertion" },
          { value: CellContentType.JSON, label: "JSON" },
        ];
      }

      if (isCompareColumn && shouldRenderDiffContent) {
        return [
          { value: CellContentType.PLAIN_TEXT, label: "Diff" },
          { value: COMPARE_TEXT_DISPLAY_MODE, label: "Text" },
        ];
      }

      return undefined;
    }, [
      assertionModel,
      isCompareColumn,
      isRequestColumn,
      mockTraceId,
      requestLogId,
      shouldRenderDiffContent,
    ]);

    const cellUpdatedAt = dialogCell?.updated_at ?? null;

    const dialogContextValue = useMemo<CellContentDialogContextValue>(
      () => ({
        column: headerColumn,
        rowIndex,
        displayedRowIndex: activeDisplayedRowIndex,
        totalRows,
        content,
        sourceContentType: content.type,
        displayType: selectedDisplayType,
        displayTypeOptions,
        setDisplayType: type => {
          if (type === TRACE_DISPLAY_MODE || type === REQUEST_LOG_DISPLAY_MODE) {
            setDisplayTypeOverride(null);
            return;
          }

          if (type === COMPARE_TEXT_DISPLAY_MODE) {
            setCompareTextMode(true);
            return;
          }

          if (type === CellContentType.PLAIN_TEXT && isCompareColumn && shouldRenderDiffContent) {
            setCompareTextMode(false);
            return;
          }

          setCompareTextMode(false);

          if (isRequestColumn && requestLogId) {
            // Request Log is the default view, so Text/JSON must be stored even when they
            // match the content's own type — a null override falls back to Request Log.
            setDisplayTypeOverride(type);
            return;
          }

          if (mockTraceId) {
            // Trace is the default view, so Text/JSON must be stored even when they match
            // the content's own type — a null override falls back to the trace.
            setDisplayTypeOverride(type);
            return;
          }

          if (assertionModel !== undefined) {
            setDisplayTypeOverride(type === ASSERTION_DISPLAY_MODE ? null : type);
            return;
          }

          setDisplayTypeOverride(
            nextDisplayTypeOverride(type, defaultDisplayType) as
              | CellContentType
              | typeof ASSERTION_DISPLAY_MODE
              | null,
          );
        },
        navigateUp: () => navigate(-1),
        navigateDown: () => navigate(+1),
        close: handleClose,
        shouldShowComputationStatus,
        effectiveStatus,
        cellUpdatedAt,
        isSavingCell: isSaving,
        isContentLoading: isDialogContentLoading,
        hideRowNavigation,
        hideSavedAt,
        headerContextLabel,
        snippetVersionsLabel,
      }),
      [
        headerColumn,
        rowIndex,
        activeDisplayedRowIndex,
        totalRows,
        content,
        defaultDisplayType,
        selectedDisplayType,
        displayTypeOptions,
        assertionModel,
        isCompareColumn,
        isRequestColumn,
        mockTraceId,
        requestLogId,
        shouldRenderDiffContent,
        navigate,
        handleClose,
        shouldShowComputationStatus,
        effectiveStatus,
        cellUpdatedAt,
        isSaving,
        isDialogContentLoading,
        hideRowNavigation,
        hideSavedAt,
        headerContextLabel,
        snippetVersionsLabel,
      ],
    );

    return (
      <CellContentDialogProvider value={dialogContextValue}>
        <Dialog
          open={isOpen}
          onOpenChange={nextOpen => {
            if (!nextOpen) {
              handleClose();
            }
          }}
        >
          <DialogContent
            ref={dialogContentRef}
            shouldShowCloseButton={false}
            motion="fade"
            overlayClassName="duration-100"
            tabIndex={-1}
            className="flex h-[min(85vh,48rem)] max-h-[85vh] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden border-0 p-0 shadow-2xl outline-none sm:max-w-4xl sm:rounded-xl"
            onOpenAutoFocus={handleOpenAutoFocus}
            onCloseAutoFocus={handleCloseAutoFocus}
            aria-describedby={undefined}
          >
            {/* Visually hidden title for screen readers (Radix requirement) */}
            <DialogTitle className="sr-only">
              {headerColumn?.title ?? colDef?.headerName ?? displayColumnId ?? "Cell"} — Row{" "}
              {visibleRowIndex + 1}
            </DialogTitle>

            {/* Header — minimal: nav, context, copy, close */}
            <CellContentHeader />

            {/* Status bar */}
            {shouldShowComputationStatus ? <CellContentStatusBar /> : null}

            {/* Content area */}
            <div
              ref={scrollContainerRef}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
            >
              {isFullCellPaused ? (
                <div className="border-b border-border/60 bg-muted/30 px-8 py-2 text-xs text-muted-foreground">
                  Waiting for connection to load the full value…
                </div>
              ) : null}
              {isFullCellFetchError ? (
                <div className="flex items-center justify-between gap-3 border-b border-destructive/20 bg-destructive/5 px-8 py-2 text-xs text-destructive">
                  <span>Could not load the full value. Editing is disabled until it loads.</span>
                  <button
                    type="button"
                    className="rounded-md border border-destructive/30 px-2 py-1 font-medium hover:bg-destructive/10"
                    onClick={() => {
                      void fullCellQuery.refetch();
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : null}
              {renderContent()}
            </div>
          </DialogContent>
        </Dialog>
      </CellContentDialogProvider>
    );
  },
);

CellContentDialog.displayName = "CellContentDialog";

export default CellContentDialog;
export { useCellContentDialog };
